import {
    FormConfig as OriginalFormConfig,
    FormState as OriginalFormState,
    Row,
    RowItemType,
    Section,
    SectionItem,
    SectionType,
    UIElementType,
    UIItem,
} from './models';
import { ButtonDisableIdentifier } from './models/validator-identifier';
import {
    FieldItem,
    BaseFieldItem,
    TextField,
    NumberField,
    SelectField,
    ToggleField,
    DateField,
    AutocompleteField,
    CheckboxField,
    RangeField,
    FieldType,
} from './models/field-models';
import { Action } from '@ngrx/store';

// Runtime-specific properties for normalized fields
export interface NormalizedFieldExtras {
    path?: string;
    errors?: { [key: string]: string };
}

// UI field specific structure
export interface NormalizedUIField {
    id: string;
    name: string;
    type: FieldType.UI;
    value: null;
    path?: string;
    visible?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    panelClass?: string;
    errors?: { [key: string]: string };
    events?: {
        [key: string]: (value: Event) => Action[];
    };
    label?: string;
    uiType: UIElementType;
    iconName?: string;
    imgSrc?: string;
    imgAlt?: string;
    imgHeight?: number;
    imgClass?: string;
    imgWidth?: number;
    enabledImgSrc?: string;
    enabledImgClass?: string;
    tooltip?: string;
    textClass?: string;
    dividerStyle?: 'solid' | 'dashed' | 'dotted';
    dividerColor?: string;
    dividerThickness?: number;
    suffixText?: string;
    suffixTextClass?: string;
    disabledSelector?: ButtonDisableIdentifier;
    preserveOldValues?: boolean;
    menuLabel?: string;
    menuIcon?: string;
    menuItems?: NormalizedFieldState[];
    componentId?: string;
}

// Section field specific structure
export interface NormalizedSectionField {
    id: string;
    name: string;
    type: 'section';
    value: null;
    path?: string;
    visible?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    errors?: { [key: string]: string };
    panelClass?: string;
    section: NormalizedSection;
    preserveOldValues?: boolean;
}

// Union type for all normalized field types
export type NormalizedFieldState =
    | (TextField & NormalizedFieldExtras)
    | (NumberField & NormalizedFieldExtras)
    | (SelectField & NormalizedFieldExtras)
    | (ToggleField & NormalizedFieldExtras)
    | (DateField & NormalizedFieldExtras)
    | (AutocompleteField & NormalizedFieldExtras)
    | (CheckboxField & NormalizedFieldExtras)
    | (RangeField & NormalizedFieldExtras)
    | NormalizedUIField
    | NormalizedSectionField;

export type NormalizedFormField = Exclude<NormalizedFieldState, NormalizedUIField | NormalizedSectionField>;

export function isFormField(field: NormalizedFieldState): field is NormalizedFormField {
    return field.type !== FieldType.UI && field.type !== FieldType.Section;
}

/**
 * Normalized row with item IDs instead of item objects
 */
export interface NormalizedRowConfig {
    id: string;
    panelClass?: string;
    tooltip?: string;
    fields: string[]; // Contains IDs of items (fields, UI elements, section fields)
}

/**
 * Normalized section with a flat fields collection
 */
export interface NormalizedSection {
    id: string;
    title: string;
    displayTitle: boolean;
    displayActionBtns: boolean;
    type?: SectionType;
    itemGenerator?: (index: number) => Row;
    expandable?: boolean;
    expanded?: boolean;
    panelClass?: string;
    parent?: {
        sectionId: string;
        rowId: string;
    };
    rows: NormalizedRowConfig[];
    fields: { [key: string]: NormalizedFieldState }; // All items by ID
    metadata?: { [key: string]: unknown };
    errors?: { [key: string]: string };
    preserveOldValues?: boolean;
    isDynamic?: boolean;
    maxItems?: number;
}

/**
 * Normalized form configuration
 */
export interface NormalizedFormConfig {
    id: string;
    title: string;
    sections: NormalizedSection[];
}

/**
 * Normalized form state
 */
export interface NormalizedFormState {
    config: NormalizedFormConfig;
    pristine?: boolean;
    valid?: boolean;
    submitted?: boolean;
    loading?: boolean;
}

// Normalization functions
export function normalizeFormConfig(originalForm: OriginalFormConfig): NormalizedFormConfig {
    const normalizedForm: NormalizedFormConfig = {
        id: originalForm.info.id,
        title: originalForm.info.title,
        sections: [],
    };

    // Process the top level sections
    originalForm.sections.forEach((section) => {
        normalizedForm.sections.push(normalizeSection(section));
    });

    return normalizedForm;
}

export function normalizeFormState(originalState: OriginalFormState): NormalizedFormState {
    return {
        config: normalizeFormConfig(originalState.config),
        pristine: originalState.pristine,
        valid: originalState.valid,
        submitted: originalState.submitted,
        loading: originalState.loading,
    };
}

// Helper function to create base normalized field properties
function createNormalizedBaseField(
    item: FieldItem | UIItem | SectionItem,
    itemPath: string,
): Pick<BaseFieldItem, 'id' | 'name'> & Partial<Omit<BaseFieldItem, 'id' | 'name'>> & NormalizedFieldExtras {
    return {
        id: item.id,
        name: 'name' in item ? item.name : item.id,
        path: itemPath,
        label: 'label' in item ? item.label : undefined,
        placeholder: 'placeholder' in item ? item.placeholder : undefined,
        placeholderSuffix: 'placeholderSuffix' in item ? item.placeholderSuffix : undefined,
        validators: 'validators' in item ? item.validators : undefined,
        events: 'events' in item ? item.events : undefined,
        visible: 'visible' in item ? item.visible : undefined,
        readonly: 'readonly' in item ? item.readonly : undefined,
        disabled: 'disabled' in item ? item.disabled : undefined,
        errors: 'errors' in item ? item.errors : undefined,
        panelClass: 'panelClass' in item ? item.panelClass : undefined,
        preserveOldValues: 'preserveOldValues' in item ? item.preserveOldValues : undefined,
    };
}

/**
 * Normalizes a single row and its items, returning both the normalized row config
 * and the field definitions that should be added to the parent section
 */
export function normalizeRow(
    row: Row,
    rowIndex: number,
    sectionPath: string,
    sectionType?: SectionType,
    parentSectionId?: string,
): {
    normalizedRow: NormalizedRowConfig;
    fields: { [key: string]: NormalizedFieldState };
} {
    const normalizedRow: NormalizedRowConfig = {
        id: row.info.id,
        panelClass: row.info.panelClass,
        tooltip: row.info.tooltip,
        fields: [],
    };

    const fields: { [key: string]: NormalizedFieldState } = {};

    // For array sections, add row index to path
    const rowPath = sectionType === 'array' ? `${sectionPath}[${rowIndex}]` : sectionPath;

    // Process each item in the row
    row.items.forEach((item) => {
        const itemId = item.id;
        normalizedRow.fields.push(itemId);

        // Create field path
        const itemPath = `${rowPath}.${itemId}`;

        // Handle different item types
        if (item.type === RowItemType.UI) {
            // UI item
            const uiItem = item as UIItem;
            const normalizedUIField: NormalizedUIField = {
                id: uiItem.id,
                name: uiItem.name || uiItem.id,
                type: FieldType.UI,
                value: null,
                path: itemPath,
                visible: uiItem.visible !== false, // Default to true if not specified
                readonly: false,
                disabled: uiItem.disabled,
                panelClass: uiItem.panelClass,
                label: uiItem.label,
                uiType: uiItem.uiType,
                iconName: uiItem.iconName,
                events: uiItem.events,
                imgSrc: uiItem.imgSrc,
                imgAlt: uiItem.imgAlt,
                imgHeight: uiItem.imgHeight,
                imgWidth: uiItem.imgWidth,
                imgClass: uiItem.imgClass,
                enabledImgSrc: uiItem.enabledImgSrc,
                enabledImgClass: uiItem.enabledImgClass,
                tooltip: uiItem.tooltip,
                textClass: uiItem.textClass,
                dividerStyle: uiItem.dividerStyle,
                dividerColor: uiItem.dividerColor,
                dividerThickness: uiItem.dividerThickness,
                suffixText: uiItem.suffixText,
                suffixTextClass: uiItem.suffixTextClass,
                disabledSelector: uiItem.disabledSelector,
                componentId: uiItem.componentId,
            };

            // Handle menu UI type
            if (uiItem.uiType === UIElementType.Menu && uiItem.menuConfig) {
                // Copy menu basic properties
                normalizedUIField.menuLabel = uiItem.menuConfig.label;
                normalizedUIField.menuIcon = uiItem.menuConfig.icon;

                // Initialize menu items array
                normalizedUIField.menuItems = [];

                // Process each menu item
                uiItem.menuConfig.items.forEach((menuItem, index) => {
                    const menuItemId = `${uiItem.id}-item-${index}`;
                    const menuItemPath = `${itemPath}.menuItems[${index}]`;

                    if (menuItem.type === RowItemType.UI) {
                        // Handle UI menu item
                        const uiMenuItem = menuItem as UIItem;
                        const normalizedMenuItem: NormalizedUIField = {
                            id: menuItemId,
                            name: uiMenuItem.name || menuItemId,
                            type: FieldType.UI,
                            value: null,
                            path: menuItemPath,
                            uiType: uiMenuItem.uiType,
                            label: uiMenuItem.label,
                            iconName: uiMenuItem.iconName,
                            events: uiMenuItem.events,
                            visible: uiMenuItem.visible !== false,
                            readonly: false,
                            disabled: uiMenuItem.disabled,
                            panelClass: uiMenuItem.panelClass,
                            tooltip: uiMenuItem.tooltip,
                            imgSrc: uiMenuItem.imgSrc,
                            imgAlt: uiMenuItem.imgAlt,
                            imgHeight: uiMenuItem.imgHeight,
                            imgWidth: uiMenuItem.imgWidth,
                            imgClass: uiMenuItem.imgClass,
                            enabledImgSrc: uiMenuItem.enabledImgSrc,
                            enabledImgClass: uiMenuItem.enabledImgClass,
                            textClass: uiMenuItem.textClass,
                            dividerStyle: uiMenuItem.dividerStyle,
                            dividerColor: uiMenuItem.dividerColor,
                            dividerThickness: uiMenuItem.dividerThickness,
                            suffixText: uiMenuItem.suffixText,
                            suffixTextClass: uiMenuItem.suffixTextClass,
                            disabledSelector: uiMenuItem.disabledSelector,
                        };
                        normalizedUIField.menuItems.push(normalizedMenuItem);
                    } else if (menuItem.type === RowItemType.Section) {
                        // Handle section menu item
                        const sectionMenuItem = menuItem as SectionItem;
                        const nestedSection = normalizeSection(
                            sectionMenuItem.section,
                            { sectionId: parentSectionId || '', rowId: row.info.id },
                            menuItemPath,
                        );
                        const normalizedSectionField: NormalizedSectionField = {
                            id: menuItemId,
                            name: menuItemId,
                            type: FieldType.Section,
                            value: null,
                            path: menuItemPath,
                            section: nestedSection,
                            visible: true,
                            disabled: false,
                            readonly: false,
                        };
                        normalizedUIField.menuItems.push(normalizedSectionField);
                    } else {
                        // Handle field menu item
                        const fieldMenuItem = menuItem as FieldItem;
                        const baseField = createNormalizedBaseField(fieldMenuItem, menuItemPath);

                        // Process field based on its type
                        switch (fieldMenuItem.type) {
                            case FieldType.Text: {
                                const normalizedField: TextField & NormalizedFieldExtras = {
                                    ...fieldMenuItem,
                                    ...baseField,
                                    id: menuItemId,
                                    value: fieldMenuItem.value ?? null,
                                };
                                normalizedUIField.menuItems.push(normalizedField);
                                break;
                            }
                            case FieldType.Number: {
                                const normalizedField: NumberField & NormalizedFieldExtras = {
                                    ...fieldMenuItem,
                                    ...baseField,
                                    id: menuItemId,
                                    value: fieldMenuItem.value ?? null,
                                };
                                normalizedUIField.menuItems.push(normalizedField);
                                break;
                            }
                            case FieldType.Select: {
                                const selectField = fieldMenuItem as SelectField;
                                const normalizedField: SelectField & NormalizedFieldExtras = {
                                    ...selectField,
                                    ...baseField,
                                    id: menuItemId,
                                    value: selectField.value ?? null,
                                };
                                normalizedUIField.menuItems.push(normalizedField);
                                break;
                            }
                            case FieldType.Toggle: {
                                const normalizedField: ToggleField & NormalizedFieldExtras = {
                                    ...(fieldMenuItem as ToggleField),
                                    ...baseField,
                                    id: menuItemId,
                                    value: fieldMenuItem.value ?? false,
                                };
                                normalizedUIField.menuItems.push(normalizedField);
                                break;
                            }
                            case FieldType.Date: {
                                const dateField = fieldMenuItem as DateField;
                                const normalizedField: DateField & NormalizedFieldExtras = {
                                    ...dateField,
                                    ...baseField,
                                    id: menuItemId,
                                    value: dateField.value ?? null,
                                };
                                normalizedUIField.menuItems.push(normalizedField);
                                break;
                            }
                            case FieldType.Autocomplete: {
                                const autocompleteField = fieldMenuItem as AutocompleteField;
                                const normalizedField: AutocompleteField & NormalizedFieldExtras = {
                                    ...autocompleteField,
                                    ...baseField,
                                    id: menuItemId,
                                    value: autocompleteField.value ?? null,
                                };
                                normalizedUIField.menuItems.push(normalizedField);
                                break;
                            }
                            case FieldType.Checkbox: {
                                const normalizedField: CheckboxField & NormalizedFieldExtras = {
                                    ...(fieldMenuItem as CheckboxField),
                                    ...baseField,
                                    id: menuItemId,
                                    value: fieldMenuItem.value ?? false,
                                };
                                normalizedUIField.menuItems.push(normalizedField);
                                break;
                            }
                            case FieldType.Range: {
                                const rangeField = fieldMenuItem as RangeField;
                                const normalizedField: RangeField & NormalizedFieldExtras = {
                                    ...rangeField,
                                    ...baseField,
                                    id: menuItemId,
                                    value: rangeField.value ?? 0,
                                };
                                normalizedUIField.menuItems.push(normalizedField);
                                break;
                            }
                        }
                    }
                });
            }

            fields[itemId] = normalizedUIField;
        } else if (item.type === RowItemType.Section) {
            // Section item
            const sectionItem = item as SectionItem;
            const nestedSection = normalizeSection(
                sectionItem.section,
                { sectionId: parentSectionId || '', rowId: row.info.id },
                itemPath,
            );
            const normalizedSectionField: NormalizedSectionField = {
                id: sectionItem.id,
                name: sectionItem.id,
                type: FieldType.Section,
                value: null,
                path: itemPath,
                section: nestedSection,
                visible: 'visible' in sectionItem ? Boolean(sectionItem.visible) : undefined,
                disabled: 'disabled' in sectionItem ? Boolean(sectionItem.disabled) : undefined,
                readonly: 'readonly' in sectionItem ? Boolean(sectionItem.readonly) : undefined,
                panelClass: 'panelClass' in sectionItem ? String(sectionItem.panelClass) : undefined,
            };
            fields[itemId] = normalizedSectionField;
        } else {
            // Field items - use type checking for specific field types
            const fieldItem = item as FieldItem;
            const baseField = createNormalizedBaseField(fieldItem, itemPath);

            // Create normalized field by extending the original field type
            switch (fieldItem.type) {
                case FieldType.Text: {
                    const normalizedField: TextField & NormalizedFieldExtras = {
                        ...fieldItem,
                        ...baseField,
                        value: fieldItem.value ?? null,
                    };
                    fields[itemId] = normalizedField;
                    break;
                }
                case FieldType.Number: {
                    const normalizedField: NumberField & NormalizedFieldExtras = {
                        ...fieldItem,
                        ...baseField,
                        value: fieldItem.value ?? null,
                    };
                    fields[itemId] = normalizedField;
                    break;
                }
                case FieldType.Select: {
                    const selectField = fieldItem as SelectField;
                    const normalizedField: SelectField & NormalizedFieldExtras = {
                        ...selectField,
                        ...baseField,
                        value: selectField.value ?? null,
                    };
                    fields[itemId] = normalizedField;
                    break;
                }
                case FieldType.Toggle: {
                    const normalizedField: ToggleField & NormalizedFieldExtras = {
                        ...(fieldItem as ToggleField),
                        ...baseField,
                        value: fieldItem.value ?? false,
                    };
                    fields[itemId] = normalizedField;
                    break;
                }
                case FieldType.Date: {
                    const dateField = fieldItem as DateField;
                    const normalizedField: DateField & NormalizedFieldExtras = {
                        ...dateField,
                        ...baseField,
                        value: dateField.value ?? null,
                    };
                    fields[itemId] = normalizedField;
                    break;
                }
                case FieldType.Autocomplete: {
                    const autocompleteField = fieldItem as AutocompleteField;
                    const normalizedField: AutocompleteField & NormalizedFieldExtras = {
                        ...autocompleteField,
                        ...baseField,
                        value: autocompleteField.value ?? null,
                    };
                    fields[itemId] = normalizedField;
                    break;
                }
                case FieldType.Checkbox: {
                    const normalizedField: CheckboxField & NormalizedFieldExtras = {
                        ...(fieldItem as CheckboxField),
                        ...baseField,
                        value: fieldItem.value ?? false,
                    };
                    fields[itemId] = normalizedField;
                    break;
                }
                case FieldType.Range: {
                    const rangeField = fieldItem as RangeField;
                    const normalizedField: RangeField & NormalizedFieldExtras = {
                        ...rangeField,
                        ...baseField,
                        value: rangeField.value ?? 0,
                    };
                    fields[itemId] = normalizedField;
                    break;
                }
            }
        }
    });

    return {
        normalizedRow,
        fields,
    };
}

export function normalizeSection(
    section: Section,
    parentInfo?: { sectionId: string; rowId: string },
    parentPath: string = '',
): NormalizedSection {
    const normalizedSection: NormalizedSection = {
        id: section.info.id,
        title: section.info.title,
        displayTitle: section.info.displayTitle,
        displayActionBtns: section.info.displayActionBtns || false,
        type: section.info.type || SectionType.Standard,
        expandable: section.info.expandable,
        expanded: section.info.expanded,
        panelClass: section.info.panelClass,
        itemGenerator: section.info.itemGenerator,
        maxItems: section.info.maxItems,
        metadata: section.info.metadata,
        errors: section.info.errors,
        preserveOldValues: section.info.preserveOldValues,
        isDynamic: section.info.isDynamic,
        rows: [],
        fields: {},
    };

    // If this is a nested section, add parent info
    if (parentInfo) {
        normalizedSection.parent = parentInfo;
    }

    // Calculate the section path
    const sectionPath = parentPath ? parentPath : section.info.id;

    // Process each row using the extracted normalize function
    section.rows.forEach((row, rowIndex) => {
        const { normalizedRow, fields } = normalizeRow(
            row,
            rowIndex,
            sectionPath,
            section.info.type,
            section.info.id,
        );

        normalizedSection.rows.push(normalizedRow);
        
        // Merge row fields into section fields collection
        Object.assign(normalizedSection.fields, fields);
    });

    return normalizedSection;
}
